import type { Schemas } from "@softmax/observatory-client/types";
import type {
  ProgramDetail,
  ProgramResult,
  StarterSource,
} from "./program-records";
const sourceTags = ["source_url", "source", "repo", "repository"];
/** The first http(s) URL among the tags authors use for a source link. */
function sourceHrefOf(
  tags: Readonly<Record<string, string>> | undefined,
): string | null {
  for (const key of sourceTags) {
    const value = tags?.[key]?.trim();

    if (value && /^https?:\/\/\S+$/i.test(value)) return value;
  }

  return null;
}
/** `name:vN` split back into its parts (raw, with any player suffix: it finds the stored row); null for a label that is not one. */
export function parseProgramLabel(label: string): {
  readonly name: string;
  readonly version: number;
} | null {
  if (!/^(.+):v(\d+)$/.test(label)) return null;

  const delimiter = label.lastIndexOf(":v");
  return {
    name: label.slice(0, delimiter),
    version: Number(label.slice(delimiter + 2)),
  };
}
export function programDetailOf(input: {
  readonly label: string;
  readonly row: Schemas["PolicyVersionRow"] | null;
  /** The leaderboard entry whose `policy_label` is this program, if any. */
  readonly entry: Schemas["LeaderboardEntryPublic"] | null;
  /** Fallback author when the version row is unreadable (the leaderboard's owner). */
  readonly by?: string | null;
  readonly results: readonly ProgramResult[] | null;
  readonly source?: StarterSource | null;
}): ProgramDetail {
  const { label, row, entry } = input;
  const tags = row?.tags;
  const description = tags?.description?.trim() || tags?.title?.trim() || null;
  return {
    id: row?.id ?? null,
    label,
    version: row?.version ?? parseProgramLabel(label)?.version ?? null,
    author:
      row?.user?.name ??
      (row?.owner_coworld_name
        ? `${row.owner_coworld_name} (the coworld)`
        : null) ??
      input.by ??
      entry?.owner_name ??
      null,
    releasedAt: row?.created_at ?? null,
    description,
    sourceHref: sourceHrefOf(tags),
    rating: entry
      ? {
          rank: entry.rank,
          score: entry.score,
          scoreLabel: entry.score_label,
          winRate: entry.win_rate ?? null,
          games: entry.episodes_played ?? null,
        }
      : null,
    results: input.results,
    source: input.source ?? null,
  };
}
